import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { addMonths, format, startOfMonth, endOfMonth, subMonths, parseISO, differenceInDays, isWithinInterval } from 'date-fns';
import CalendarDetailModal from '../components/CalendarDetailModal';
import Papa from 'papaparse';

const { FiChevronLeft, FiChevronRight, FiDownload, FiSearch, FiTrendingUp, FiAlertCircle, FiHome, FiCalendar } = FiIcons;

const Calendar = () => {
  const { data } = useStore();
  const properties = data.properties || [];
  const leases = data.leases || [];
  const [viewMonths, setViewMonths] = useState(6);
  const [startPoint, setStartPoint] = useState(startOfMonth(new Date()));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  const months = useMemo(() => {
    return Array.from({ length: viewMonths }, (_, i) => addMonths(startPoint, i));
  }, [viewMonths, startPoint]);

  const filteredProperties = properties.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get ALL leases for a property (current + historical)
  const getPropertyLeases = (prop) => {
    const propLeases = leases.filter(l => l.propertyId === prop.id || l.id === prop.currentLeaseId);
    // Also include property-level dates as a fallback lease
    if (propLeases.length === 0 && prop.leaseStart && prop.leaseEnd) {
      propLeases.push({ startDate: prop.leaseStart, endDate: prop.leaseEnd, tenantId: prop.tenantId, rentAmount: prop.rentRate, id: 'prop_' + prop.id });
    }
    return propLeases;
  };

  // Get the active lease for a property in a given month
  const getLeaseForMonth = (prop, monthDate) => {
    const propLeases = getPropertyLeases(prop);
    const mStart = startOfMonth(monthDate);
    const mEnd = endOfMonth(monthDate);
    return propLeases.find(l => {
      if (!l.startDate || !l.endDate) return false;
      return parseISO(l.startDate) <= mEnd && parseISO(l.endDate) >= mStart;
    });
  };

  // Get the current/most recent lease
  const getCurrentLease = (prop) => {
    const propLeases = getPropertyLeases(prop);
    if (propLeases.length === 0) return null;
    // Sort by end date descending, return the latest
    return propLeases.sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
  };

  // Check occupancy for a property in a given month
  const getOccupancyStatus = (prop, monthDate) => {
    const lease = getLeaseForMonth(prop, monthDate);
    if (!lease) return 'vacant';

    const leaseEnd = parseISO(lease.endDate);
    const leaseStart = parseISO(lease.startDate);

    const endMonth = format(leaseEnd, 'yyyy-MM');
    const startMonth = format(leaseStart, 'yyyy-MM');
    const thisMonth = format(monthDate, 'yyyy-MM');

    if (endMonth === thisMonth) return 'ending';
    if (startMonth === thisMonth) return 'starting';

    return 'occupied';
  };

  // Monthly stats
  const getMonthStats = (monthDate) => {
    let occupied = 0;
    let revenue = 0;
    filteredProperties.forEach(p => {
      const status = getOccupancyStatus(p, monthDate);
      if (status !== 'vacant') {
        occupied++;
        const lease = getLeaseForMonth(p, monthDate);
        revenue += Number(lease?.rentAmount || p.rentRate || 0);
      }
    });
    return { occupied, vacant: filteredProperties.length - occupied, revenue, rate: filteredProperties.length ? Math.round((occupied / filteredProperties.length) * 100) : 0 };
  };

  // Per-property occupancy rate across visible months
  const getPropertyOccupancyRate = (prop) => {
    const occupiedMonths = months.filter(m => getOccupancyStatus(prop, m) !== 'vacant').length;
    return Math.round((occupiedMonths / months.length) * 100);
  };

  // Overall stats
  const overallStats = useMemo(() => {
    const today = new Date();
    const currentMonth = getMonthStats(today);
    const totalPotential = filteredProperties.reduce((s, p) => s + Number(p.rentRate || 0), 0);
    const vacantNow = filteredProperties.filter(p => getOccupancyStatus(p, today) === 'vacant');
    const endingSoon = filteredProperties.filter(p => {
      const lease = getCurrentLease(p);
      if (!lease?.endDate) return false;
      const days = differenceInDays(parseISO(lease.endDate), today);
      return days >= 0 && days <= 30;
    });
    return { ...currentMonth, totalPotential, vacantNow, endingSoon };
  }, [filteredProperties, leases]);

  const handleExport = () => {
    const exportData = filteredProperties.map(p => {
      const row = { Property: p.name, Address: p.address };
      months.forEach(m => {
        row[format(m, 'MMM yyyy')] = getOccupancyStatus(p, m) === 'vacant' ? 'VACANT' : 'OCCUPIED';
      });
      row['Occupancy Rate'] = getPropertyOccupancyRate(p) + '%';
      return row;
    });
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `occupancy_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const statusColors = {
    occupied: { bg: 'bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    starting: { bg: 'bg-blue-100', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
    ending: { bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
    vacant: { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-400', dot: 'bg-red-300' },
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Occupancy Calendar</h2>
          <p className="text-sm text-gray-500">At-a-glance property availability and lease timeline.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-white rounded-lg p-0.5 border border-gray-200 shadow-sm">
            {[6, 12, 18].map(m => (
              <button key={m} onClick={() => setViewMonths(m)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${viewMonths === m ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                {m}M
              </button>
            ))}
          </div>
          <button onClick={handleExport} className="flex items-center px-3 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition text-xs font-semibold">
            <SafeIcon icon={FiDownload} className="mr-1.5" size={13} /> Export
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Current Occupancy</p>
          <div className="flex items-end gap-2">
            <p className="text-2xl font-bold text-gray-900">{overallStats.rate}%</p>
            <p className="text-xs text-gray-400 mb-0.5">{overallStats.occupied}/{filteredProperties.length}</p>
          </div>
          <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
            <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${overallStats.rate}%` }} />
          </div>
        </div>
        <div className={`p-4 rounded-xl border shadow-sm ${overallStats.vacantNow.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Vacant Now</p>
          <p className={`text-2xl font-bold ${overallStats.vacantNow.length > 0 ? 'text-red-600' : 'text-gray-300'}`}>{overallStats.vacantNow.length}</p>
          {overallStats.vacantNow.length > 0 && (
            <p className="text-[10px] text-red-500 mt-1 truncate">{overallStats.vacantNow.map(p => p.name).join(', ')}</p>
          )}
        </div>
        <div className={`p-4 rounded-xl border shadow-sm ${overallStats.endingSoon.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Ending in 30d</p>
          <p className={`text-2xl font-bold ${overallStats.endingSoon.length > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{overallStats.endingSoon.length}</p>
          {overallStats.endingSoon.length > 0 && (
            <p className="text-[10px] text-amber-500 mt-1 truncate">{overallStats.endingSoon.map(p => p.name).join(', ')}</p>
          )}
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Active Revenue</p>
          <p className="text-2xl font-bold text-emerald-600">${overallStats.revenue.toLocaleString()}</p>
          <p className="text-[10px] text-gray-400 mt-1">/month collected</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Vacancy Loss</p>
          <p className="text-2xl font-bold text-red-500">${(overallStats.totalPotential - overallStats.revenue).toLocaleString()}</p>
          <p className="text-[10px] text-gray-400 mt-1">/month lost revenue</p>
        </div>
      </div>

      {/* Revenue by Month */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {months.slice(0, 6).map((m, i) => {
          const stats = getMonthStats(m);
          return (
            <div key={i} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-semibold text-gray-400 uppercase">{format(m, 'MMM yyyy')}</p>
              <p className="text-sm font-bold text-gray-900 mt-0.5">${stats.revenue.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-1">
                <div className="flex-1 bg-gray-100 h-1 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${stats.rate}%` }} />
                </div>
                <span className="text-[9px] font-bold text-gray-400">{stats.rate}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-3 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="relative w-full md:w-72">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input type="text" placeholder="Search properties..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setStartPoint(subMonths(startPoint, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg transition">
              <SafeIcon icon={FiChevronLeft} size={16} />
            </button>
            <button onClick={() => setStartPoint(startOfMonth(new Date()))} className="px-3 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
              Today
            </button>
            <button onClick={() => setStartPoint(addMonths(startPoint, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg transition">
              <SafeIcon icon={FiChevronRight} size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 py-3 px-4 text-left text-[10px] font-semibold text-gray-400 uppercase w-52 border-b border-gray-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                  Property
                </th>
                {months.map((m, i) => {
                  const stats = getMonthStats(m);
                  return (
                    <th key={i} className="py-3 px-2 text-center border-b border-gray-100 min-w-[120px]">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase">{format(m, 'MMM yyyy')}</p>
                      <p className="text-[9px] text-gray-300 mt-0.5">{stats.occupied} of {filteredProperties.length} occupied</p>
                    </th>
                  );
                })}
                <th className="sticky right-0 z-10 bg-gray-50 py-3 px-3 text-center text-[10px] font-semibold text-gray-400 uppercase w-20 border-b border-gray-100 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                  Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredProperties.map(p => {
                const occRate = getPropertyOccupancyRate(p);
                const lease = getCurrentLease(p);
                const tenant = data.tenants.find(t => t.id === (lease?.tenantId || p.tenantId));

                return (
                  <tr key={p.id} className="hover:bg-gray-50/30 transition-colors">
                    <td className="sticky left-0 z-10 bg-white py-3 px-4 border-r border-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.03)]">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${p.status === 'Occupied' ? 'bg-emerald-500' : 'bg-red-400'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                          <p className="text-[10px] text-gray-400 truncate">{p.address}</p>
                        </div>
                      </div>
                    </td>
                    {months.map((m, i) => {
                      const status = getOccupancyStatus(p, m);
                      const colors = statusColors[status];

                      return (
                        <td key={i} className="px-1.5 py-2">
                          <div
                            onClick={() => {
                              const monthLease = getLeaseForMonth(p, m);
                              if (status !== 'vacant' && monthLease) {
                                setSelectedBooking({ property: p, start: monthLease.startDate, end: monthLease.endDate });
                              }
                            }}
                            className={`h-10 rounded-lg border flex items-center justify-center gap-1 transition-all ${colors.bg} ${colors.border} ${status !== 'vacant' ? 'cursor-pointer hover:scale-[1.03]' : ''}`}
                          >
                            {status === 'vacant' ? (
                              <span className={`text-[9px] font-semibold ${colors.text} uppercase`}>Vacant</span>
                            ) : status === 'ending' ? (
                              <div className="flex items-center gap-1">
                                <SafeIcon icon={FiAlertCircle} size={10} className="text-amber-500" />
                                <span className="text-[9px] font-bold text-amber-700">Ending</span>
                              </div>
                            ) : status === 'starting' ? (
                              <div className="flex items-center gap-1">
                                <SafeIcon icon={FiCalendar} size={10} className="text-blue-500" />
                                <span className="text-[9px] font-bold text-blue-700">Move-in</span>
                              </div>
                            ) : (
                              <span className={`text-[9px] font-bold ${colors.text}`}>
                                {tenant?.name?.split(' ')[0] || p.tenantName?.split(' ')[0] || 'Booked'}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 bg-white py-3 px-3 text-center border-l border-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.03)]">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${
                        occRate >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        occRate >= 50 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {occRate}%
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredProperties.length === 0 && (
                <tr>
                  <td colSpan={months.length + 2} className="py-16 text-center text-sm text-gray-400">No properties found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="p-4 border-t border-gray-100 flex flex-wrap items-center gap-4">
          {[
            { label: 'Occupied', ...statusColors.occupied },
            { label: 'Move-in Month', ...statusColors.starting },
            { label: 'Lease Ending', ...statusColors.ending },
            { label: 'Vacant', ...statusColors.vacant },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${item.bg} border ${item.border}`} />
              <span className="text-[10px] font-semibold text-gray-500">{item.label}</span>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-3 text-[10px] font-semibold text-gray-400">
            <span>Rate: </span>
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">80%+</span>
            <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">50-79%</span>
            <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">&lt;50%</span>
          </div>
        </div>
      </div>

      <CalendarDetailModal
        isOpen={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        property={selectedBooking?.property}
        leaseStart={selectedBooking?.start}
        leaseEnd={selectedBooking?.end}
      />
    </div>
  );
};

export default Calendar;
